import type { ChatMessages } from "@storytell/cp-client";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { TbExternalLink, TbGlobe } from "solid-icons/tb";
import { type Component, Show, createMemo } from "solid-js";
import type { WebSearchResultsCollection } from "~/utils/citationResolver";
import { resolveWebCitation } from "~/utils/citationResolver";
import { parseWebCitation } from "~/utils/citationUtils";
import { extractMarkdownText } from "~/utils/textUtils";

export interface WebCitationRendererProps {
  node: { attrs: { value: string } };
  selected?: boolean;
  webSearchResults?: WebSearchResultsCollection;
}

/**
 * Renders web search citations like <cite>web_toolexecution_abc123_22</cite> as clickable links
 * with tooltips showing the title and URL
 */
export const WebCitationRenderer: Component<WebCitationRendererProps> = (props) => {
  // Parse the citation to extract toolId and index
  const parsedCitation = createMemo(() => {
    return parseWebCitation(props.node.attrs.value);
  });

  // Find the corresponding web search result using the new resolution logic
  const webResult = createMemo(() => {
    const parsed = parsedCitation();
    if (!parsed) {
      return null;
    }
    return resolveWebCitation(props.node.attrs.value, props.webSearchResults);
  });

  // Get the display index from the parsed citation
  const citationIndex = createMemo(() => {
    return parsedCitation()?.index ?? null;
  });

  const handleClick = () => {
    const result = webResult();
    if (result?.url) {
      window.open(result.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Show
      when={webResult()}
      fallback={<span class="text-muted-text">[{parsedCitation()?.index ?? props.node.attrs.value}]</span>}
    >
      {(result) => (
        <StTooltip
          placement="top"
          content={
            <div class="px-2 py-2 max-w-xs">
              <p class="font-medium text-xs mb-1">Web Source</p>
              <button
                type="button"
                onClick={handleClick}
                class="text-sm font-medium mb-1 text-blue-600 dark:text-blue-400 hover:underline cursor-pointer text-left w-full"
              >
                {result().title || "Untitled"}
              </button>
              <p class="text-xs text-green-600 dark:text-green-400 truncate">{result().url}</p>
              <Show when={result().text}>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-3">
                  {extractMarkdownText(result().text)}
                </p>
              </Show>
              <div class="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <p class="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <TbExternalLink class="size-3" />
                  Click to open in new tab
                </p>
              </div>
            </div>
          }
        >
          <StTooltipTrigger
            as="button"
            type="button"
            class="inline-flex items-center gap-0.5 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-500 transition-colors"
            data-citation={props.node.attrs.value}
          >
            <TbGlobe class="inline size-4" />
            <sup class="text-xs font-medium">{citationIndex()}</sup>
          </StTooltipTrigger>
        </StTooltip>
      )}
    </Show>
  );
};
